import axios from "axios";
import { put, takeLatest } from "redux-saga/effects";

import {
    allProposedRegulationsReceived,
    dataForFiltersReceived,
    filterDataForSensorIdsRecevied,
    toggleActionPerformed
} from "../../actions/regulation/Listview";
import {
    DeleteRegulationsAction,
    DELETE_REGULATIONS,
    ExportRegulationsAction,
    EXPORT_REGULATIONS,
    GetAllProposedRegulationsAction,
    GetDataForFiltersAction,
    GET_ALL_PROPOSED_REGULATIONS,
    GET_DATA_FOR_FILTERS,
    PublishMultipleRegulationsAction,
    PUBLISH_REGULATIONS,
    SelectedRows,
    DataForFiltersReceivedAction,
    AllProposedRegulationsReceivedAction,
    DeleteParkingRatesBatchAction,
    DELETE_PARKING_RATES_BATCH,
    GET_FILTER_DATA_FOR_SENSOR_IDS,
    GetFilterDataForSensorIdsAction,
    FilterDataForSensorIdsReceivedAction
} from "../../actions/regulation/Listview/types";
import { showError } from "../../components/commonUtilities";
import { parseDateWithDots } from "../../Utilities/dateTimeUtilities";
import {
    exportFileExtention,
    exportFileType
} from "../../components/curb/ManagerAnalyzer/Manager/ListviewTable/helpers";
import { GeneratorYieldType } from "../../interfaces/ActionReducer/GetMethod/GetResponse";
import { map } from "lodash";
import { showToast } from "components/DesignSystem";
import { decrypt } from "Utilities/decryption";

function* getAllProposedRegulation(
    action: GetAllProposedRegulationsAction
): Generator<
    | Promise<AllProposedRegulationsReceivedAction>
    | GeneratorYieldType<AllProposedRegulationsReceivedAction["payload"]>,
    void,
    AllProposedRegulationsReceivedAction["payload"]["allProposedRegulationsReceived"]
> {
    let url = "/api/proposed_regulation/?";
    const { params } = action.payload;

    if (!Object.keys(params).includes("scenario__id")) {
        return;
    }

    Object.keys(params).forEach((prop: string) => {
        if (Array.isArray(params[prop])) {
            let allFilters = params[prop].toString();
            url += `&${prop}=${allFilters}`;
        } else {
            url += `&${prop}=${params[prop]}`;
        }
    });
    url += `&derived_from_existing=false&payment__source__in=Regulation,Batch_edit,Batch_create`;

    try {
        const json = yield axios.get(url).then((response) => response.data);
        json.results.map((i: any) => {
            i.sub_activity.has_multiple_regulations =
                i.proposedregulationlinks_set.length && i.proposedregulationlinks_set[0]?.batch
                    ? i.proposedregulationlinks_set[0]?.batch.has_multiple_regulations
                    : false;
        });

        yield put(allProposedRegulationsReceived({ allProposedRegulationsReceived: json }));
    } catch (error) {
        showError(error);
    }
}

export function* getAllProposedRegulationSaga() {
    yield takeLatest(GET_ALL_PROPOSED_REGULATIONS, getAllProposedRegulation);
}

function* publishMultipleRegulations(action: PublishMultipleRegulationsAction) {
    try {
        const makeSynchronousRequests = (arr: any) => {
            let index = 0;
            const paramsForNextRequest = () => {
                index++;
                if (index - 1 === arr.length - 1) {
                    action.payload.afterPublish();
                    return;
                }
                return request();
            };

            const request = (): any => {
                let batchAPI = "";
                if (action.payload.batchIds) {
                    batchAPI =
                        arr[index].isIncrement || arr[index].deleteBatch
                            ? `/api/proposed_regulation/${arr[index].id}/publish_batch_regulation/`
                            : `/api/proposed_regulation/${arr[index].regId}/publish_proposed_regulation/`;
                } else {
                    batchAPI = `/api/proposed_regulation/${arr[index]}/publish_proposed_regulation/`;
                }
                return axios
                    .post(batchAPI)
                    .then(() => {
                        paramsForNextRequest();
                    })
                    .catch((err) => {
                        if (!err?.response?.data[0]?.includes("already published")) {
                            showError(err);
                        }
                        paramsForNextRequest();
                    });
            };
            return request();
        };

        yield makeSynchronousRequests(action.payload.batchIds ?? action.payload.regulationIds);
        showToast("File has been published successfully", { type: "success" });
    } catch (error) {
        showError(error);
    }
}

export function* publishMultipleRegulationsSaga() {
    yield takeLatest(PUBLISH_REGULATIONS, publishMultipleRegulations);
}

function* exportMultipleRegulations(
    action: ExportRegulationsAction
): Generator<GeneratorYieldType<undefined> | Promise<any>, void, any> {
    try {
        const blobFileTypes = new Set([exportFileType.pdf, exportFileType.excel]);
        const json = yield axios
            .get("/api/cityinfo/currentcity/")
            .then((response) => decrypt(response.data));
        const fileName: string = `Proposed_Changes-${json.name}-${parseDateWithDots(new Date())}`;
        yield axios
            .post(
                `/api/proposed_regulation/export_proposed_${action.payload.fileType}/`,
                {
                    regulations: action.payload.regulationIds,
                    fields: action.payload.fields,
                    map_data_url: action.payload.mapDataURL
                },
                {
                    responseType: blobFileTypes.has(action.payload.fileType) ? "blob" : "json",
                    withCredentials: true
                }
            )
            .then((res) => {
                const link = document.createElement("a");
                const blob = res.data;
                if (blobFileTypes.has(action.payload.fileType)) {
                    //for excel and pdf files
                    link.href = window.URL.createObjectURL(blob);
                } else {
                    //for geojson files
                    link.href = `data:text/json;charset=utf-8,${encodeURIComponent(
                        JSON.stringify(blob)
                    )}`;
                }
                link.download = fileName + exportFileExtention[action.payload.fileType];

                link.click();
                link.remove();
            })
            .finally(() => {
                action.payload.afterExport();
            });

        yield put(toggleActionPerformed());
    } catch (error) {
        showError(error);
    }
}

export function* exportMultipleRegulationsSaga() {
    yield takeLatest(EXPORT_REGULATIONS, exportMultipleRegulations);
}

function* deleteRegulations(action: DeleteRegulationsAction) {
    try {
        yield Promise.all(
            action.payload.regulationIds.map((id) =>
                axios.delete(`/api/proposed_regulation/${id}/delete_proposed_regulation/`)
            )
        ).catch((e) => showError(e));
        if (action.payload.afterDelete) {
            action.payload.afterDelete();
        }
    } catch (error) {
        showError(error);
    }
}

export function* deleteRegulationsSaga() {
    yield takeLatest(DELETE_REGULATIONS, deleteRegulations);
}

function* deleteParkingRatesBatch(action: DeleteParkingRatesBatchAction) {
    try {
        yield axios
            .request({
                method: "delete",
                url: "/api/batchregulations/delete_batch_regulation/",
                data: { batch_ids: action.payload.batch_ids }
            })
            .catch((e) => showError(e));
        if (action.payload.afterDelete) {
            action.payload.afterDelete();
        }
    } catch (error) {
        showError(error);
    }
}
export function* deleteParkingRatesBatchSaga() {
    yield takeLatest(DELETE_PARKING_RATES_BATCH, deleteParkingRatesBatch);
}

function* getDataForFilters(
    action: GetDataForFiltersAction
): Generator<
    | Promise<DataForFiltersReceivedAction>
    | GeneratorYieldType<DataForFiltersReceivedAction["payload"]>,
    void,
    DataForFiltersReceivedAction["payload"]["data"]
> {
    try {
        const json = yield axios
            .get(`/api/proposed_regulation/proposed_regulation_list_data/`, {
                params: action.payload.params
            })
            .then((response) => response.data);
        yield put(dataForFiltersReceived({ data: json }));
    } catch (error) {
        showError(error);
    }
}

export function* getDataForFiltersSaga() {
    yield takeLatest(GET_DATA_FOR_FILTERS, getDataForFilters);
}

function* getFilterDataForSensorIDs(
    action: GetFilterDataForSensorIdsAction
): Generator<
    | Promise<FilterDataForSensorIdsReceivedAction>
    | GeneratorYieldType<FilterDataForSensorIdsReceivedAction["payload"]>,
    void,
    FilterDataForSensorIdsReceivedAction["payload"]["data"]
> {
    try {
        const json = yield axios
            .get(
                `/api/proposed_regulation/get_objectIds/?&scenario_id=${action.payload.scenario_id}`
            )
            .then((response) => response.data);
        yield put(filterDataForSensorIdsRecevied({ data: json }));
    } catch (error) {
        showError(error);
    }
}

export function* getFilterDataForSensorIDsSaga() {
    yield takeLatest(GET_FILTER_DATA_FOR_SENSOR_IDS, getFilterDataForSensorIDs);
}
